import React, { useState } from 'react';
import { TrendingUp, PiggyBank, Calendar, ArrowDownCircle, Download } from 'lucide-react';
import { SipCalculator } from '@/components/calculators/SipCalculator';
import { LumpsumCalculator } from '@/components/calculators/LumpsumCalculator';
import { RetirementCalculator } from '@/components/calculators/RetirementCalculator';
import { SwpCalculator } from '@/components/calculators/SwpCalculator';
import { MF_DISCLAIMER } from '@/components/ComplianceNotice';

const CALCULATORS = [
  { id: 'sip', name: 'SIP Calculator', icon: TrendingUp, Component: SipCalculator },
  { id: 'lumpsum', name: 'Lumpsum Calculator', icon: PiggyBank, Component: LumpsumCalculator },
  { id: 'retirement', name: 'Retirement Calculator', icon: Calendar, Component: RetirementCalculator },
  { id: 'swp', name: 'SWP Calculator', icon: ArrowDownCircle, Component: SwpCalculator },
];

const RESOURCES = [
  "Beginner's Guide to Mutual Funds",
  'SIP Planning Checklist',
  'Retirement Planning Checklist',
  'Portfolio Review Checklist',
  'Risk Profile Questionnaire',
  'Financial Goal Worksheet',
];

const Resources = () => {
  const [activeId, setActiveId] = useState('sip');
  const Active = CALCULATORS.find((c) => c.id === activeId).Component;

  return (
    <div className="min-h-screen" data-testid="resources-page">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="resources-heading">
            Investment Calculators & Resources
          </h1>
          <p className="text-base font-body text-[#4A4A4A] max-w-3xl mx-auto leading-relaxed">
            Explore illustrative scenarios with our interactive calculators. Results are estimates based on the assumptions you enter and are not guaranteed.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div>
              <h2 className="text-2xl font-heading font-medium text-[#A0451E] mb-6">Calculators</h2>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                {CALCULATORS.map(({ id, name, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveId(id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm sm:text-base transition-colors ${
                      activeId === id ? 'bg-[#A0451E] text-white' : 'bg-[#FDF8F5] text-[#4A4A4A] hover:bg-[#A0451E]/10'
                    }`}
                    data-testid={`calculator-tab-${id}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {name}
                  </button>
                ))}
              </div>

              <div className="mt-8 p-6 bg-[#FDF8F5] rounded-xl border border-[#E5E5E5]" data-testid="calculator-risk-disclosure">
                <p className="text-xs font-body text-[#737373] leading-relaxed">
                  <strong>Risk disclosure:</strong> All calculations are illustrative and use assumed rates of return that you choose. They do not
                  represent the past or future performance of any mutual fund scheme and are not guaranteed. Actual returns depend on market
                  conditions and may be negative. {MF_DISCLAIMER}
                </p>
              </div>
            </div>

            <div className="lg:col-span-2">
              <Active />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 section-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-8 text-center">Downloadable Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {RESOURCES.map((resource, index) => (
              <div
                key={resource}
                className="bg-white rounded-xl p-6 border border-[#E5E5E5] card-hover cursor-pointer flex items-center justify-between"
                data-testid={`resource-${index}`}
              >
                <div>
                  <h3 className="font-body font-semibold text-[#A0451E] mb-1">{resource}</h3>
                  <p className="text-xs font-body text-[#737373]">PDF Download</p>
                </div>
                <Download className="w-5 h-5 text-[#D97706]" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
